import { isEmpty } from 'lodash'
import React, { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/browser'

import TokensService from '@services/tokensService'

import { Loader } from '@infologistics/frontend-libraries'

import { useAppDispatch, useAppSelector } from '@hooks/redux'
import { setLoaded } from '@store/modules/startup'
import { getLocales, getTimeZones } from '@store/modules/metadata'
import { getCurrentUser, getMandates, getSettingsUser } from '@store/modules/user'
import { setLanguage } from '@store/modules/utils'
import { catchThunkError, displayErrorNotification } from '@utils/utils'

import { useUpdateLanguage } from '@hooks/useUpdateLanguage';
import { IStartupProps as IProps } from './types'
import { getBoxes, updateBoxLogo } from '@app/store/modules/boxes'
import BoxesService from '@services/boxes';
import { ASIDE_MENU_WIDTH, Instance } from '@app/const/consts'

const Startup: FC<IProps> = ({ children }) => {
  const dispatch = useAppDispatch()
  const { i18n } = useTranslation()
  const { updateLanguage } = useUpdateLanguage()

  if (process.env.REACT_APP_API_ENV === 'development') {
    window.setLanguage = (language: string): Promise<void> => (
      i18n.changeLanguage(language)
        .then(() => {
          dispatch(setLanguage(language))
        })
    )
  }

  const { isLoaded } = useAppSelector((state) => state.startup)
  const {
    isAuthenticated,
    profile: {
      email: userEmail,
      oguid: userOguid
    }
  } = useAppSelector((state) => state.user)
  const { language } = useAppSelector((state) => state.utils)
  const { timeZones } = useAppSelector(state => state.metadata)
  const personalOguid = useAppSelector((state) => state.user.profile.boxOguid);
  const currentBoxOguid = useAppSelector((state) => state.envelopes.currentBoxOguid);
  const boxes = useAppSelector((state) => state.boxes.boxes);

  const instanceRu = useAppSelector((state) => state.utils.instance) === Instance.RU;

  const currentLogo = boxes.find((val) => val.oguid === currentBoxOguid)?.logo;

  const authUser = async (): Promise<void> => {
    const { accessToken, refreshToken } = TokensService.getTokensToStartup()

    // If there are valid tokens, then user authorization
    if (accessToken ?? refreshToken) {
      try {
        // If the access token is missing for some reason (expired),
        // then update it based on a valid refresh token
        if (refreshToken && !accessToken) {
          const isGenerateNewRefresh = TokensService.isGenerateNewRefresh(refreshToken)

          await TokensService.refresh(refreshToken, isGenerateNewRefresh)
        }

        const profile = await dispatch(getCurrentUser()).unwrap();
        const settings = await dispatch(getSettingsUser()).unwrap();
        await dispatch(getBoxes());
        instanceRu && await dispatch(getMandates());

        Sentry.configureScope((scope) => {
          scope.setUser({
            email: userEmail ?? '',
            id: userOguid
          })
        })

        updateLanguage(profile)

        const asideWidth = settings?.asideWidth

        const getAsideWidth = (): string =>
          !asideWidth ||
          typeof asideWidth !== 'string' ||
          isNaN(+asideWidth?.replace('rem', '')) ||
          +asideWidth?.replace('rem', '') < ASIDE_MENU_WIDTH.MIN
            ? ASIDE_MENU_WIDTH.MIN + 'rem'
            : +asideWidth?.replace('rem', '') > ASIDE_MENU_WIDTH.MAX
            ? ASIDE_MENU_WIDTH.MAX + 'rem'
            : asideWidth;

        document.documentElement.style.setProperty(
          '--aside-width',
          getAsideWidth()
        );

      } catch (data) {
        catchThunkError(data)
      }
    }

    dispatch(setLoaded())
  }

  useEffect(() => {
    TokensService.setTab()

    TokensService.clearBeforeUnloadTab()
  })

  useEffect(() => {
    !isLoaded && authUser().catch(displayErrorNotification)
  }, [isAuthenticated, isLoaded])

  useEffect(() => {
    dispatch(getLocales(language)).unwrap().catch(catchThunkError);

    if (language || isEmpty(timeZones)) {
      dispatch(getTimeZones()).unwrap().catch(catchThunkError);
    }
  }, [language, isAuthenticated]);

  const getBoxLogo = async (boxOguid: string) => {
    await BoxesService.getLogo(boxOguid)
      .then((res) => {
        const objectUrl = URL.createObjectURL(res.data);
        dispatch(
          updateBoxLogo({
            boxOguid: currentBoxOguid,
            logo: objectUrl,
          })
        );
      })
      .catch(() => {
        dispatch(
          updateBoxLogo({
            boxOguid: currentBoxOguid,
            logo: null,
          })
        );
      });
  };

  useEffect(() => {
    if (currentBoxOguid && currentBoxOguid !== personalOguid && !currentLogo) {
      getBoxLogo(currentBoxOguid);
    }
  }, [currentBoxOguid, personalOguid, currentLogo]);

  return isLoaded ? children : <Loader loading />
}

export default Startup
